import { Component } from 'react';
import { ConverterLayout, layoutStyles } from '../converter-layout/converter-layout';
import { convertFont, FontFormat, sniffFontFormat, SourceFormat } from './font-convert';

const FORMATS: FontFormat[] = ['ttf', 'otf', 'woff', 'woff2', 'eot'];

interface State {
    fromFormat: FontFormat;
    toFormat: FontFormat;
    fontBuffer: ArrayBuffer | null;
    fontName: string;
    outputUrl: string | null;
    outputSize: number;
    error: string | null;
    warning: string | null;
    busy: boolean;
}

function parseSlug(slug: string | undefined): { from: FontFormat; to: FontFormat } | null {
    if (!slug) return null;
    // Format hub ({ext}-converter): from = the hub's format, to = a sensible default.
    const hub = slug.match(/^([a-z0-9]+)-converter$/);
    if (hub) {
        const from = hub[1] as FontFormat;
        if (!FORMATS.includes(from)) return null;
        return { from, to: (from === 'woff2' ? 'ttf' : 'woff2') as FontFormat };
    }
    const m = slug.match(/^convert-([a-z0-9]+)-to-([a-z0-9]+)$/);
    if (!m) return null;
    const f = m[1] as FontFormat, t = m[2] as FontFormat;
    if (!FORMATS.includes(f) || !FORMATS.includes(t)) return null;
    return { from: f, to: t };
}

export default class FontConverterComponent extends Component<{ feature?: string }, State> {
    constructor(props: { feature?: string }) {
        super(props);
        const parsed = parseSlug(props.feature) || { from: 'ttf', to: 'woff2' };
        this.state = {
            fromFormat: parsed.from,
            toFormat: parsed.to,
            fontBuffer: null,
            fontName: '',
            outputUrl: null,
            outputSize: 0,
            error: null,
            warning: null,
            busy: false,
        };
    }

    componentWillUnmount() {
        if (this.state.outputUrl) URL.revokeObjectURL(this.state.outputUrl);
    }

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            const buffer = reader.result as ArrayBuffer;
            // The From dropdown frames the page; the bytes decide what we read.
            // Sync the dropdown to the file so the labels never lie about it.
            const detected: SourceFormat = sniffFontFormat(new Uint8Array(buffer));
            if (this.state.outputUrl) URL.revokeObjectURL(this.state.outputUrl);
            this.setState(prev => ({
                fontBuffer: buffer,
                fontName: f.name,
                fromFormat: FORMATS.includes(detected as FontFormat) ? detected as FontFormat : prev.fromFormat,
                outputUrl: null,
                outputSize: 0,
                error: null,
                warning: null,
            }));
        };
        reader.readAsArrayBuffer(f);
    };

    convert = async () => {
        if (!this.state.fontBuffer) return;
        this.setState({ busy: true, error: null, warning: null });
        try {
            const result = await convertFont(new Uint8Array(this.state.fontBuffer), this.state.toFormat);
            const blob = new Blob([result.data as any], { type: result.mimeType });
            if (this.state.outputUrl) URL.revokeObjectURL(this.state.outputUrl);
            this.setState({
                outputUrl: URL.createObjectURL(blob),
                outputSize: blob.size,
                warning: result.warning,
                error: null,
                busy: false,
            });
        } catch (e: any) {
            this.setState({ error: e?.message || 'Conversion failed', outputUrl: null, outputSize: 0, busy: false });
        }
    };

    download = () => {
        if (!this.state.outputUrl) return;
        const a = document.createElement('a');
        a.href = this.state.outputUrl;
        a.download = `output.${this.state.toFormat}`;
        a.click();
    };

    render() {
        const { fromFormat, toFormat, fontBuffer, fontName, outputUrl, outputSize, error, warning, busy } = this.state;
        const inputPanel = (
            <div className={layoutStyles.area}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.75rem', minHeight: 320 }}>
                {fontBuffer ? <>
                    <div style={{ fontSize: '2.5rem' }}>🅰</div>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{fontName}</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{(fontBuffer.byteLength / 1024).toFixed(1)} KB · {fromFormat.toUpperCase()}</div>
                </> : <>
                    <div style={{ fontSize: '2.5rem', opacity: 0.4 }}>🅰</div>
                    <div style={{ color: '#64748b' }}>Choose a {fromFormat.toUpperCase()} font file to convert</div>
                </>}
            </div>
        );

        const outputPanel = (
            <div className={layoutStyles.area}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.75rem', minHeight: 320, padding: '1rem' }}>
                {outputUrl ? <>
                    <div style={{ fontSize: '2.5rem' }}>🅰</div>
                    <div style={{ fontWeight: 600, color: '#065f46' }}>Converted</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{(outputSize / 1024).toFixed(1)} KB · {toFormat.toUpperCase()}</div>
                    {warning && (
                        <div role="status" style={{ fontSize: '0.8rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '0.5rem 0.75rem', maxWidth: 420 }}>
                            {warning}
                        </div>
                    )}
                </> : <>
                    <div style={{ fontSize: '2.5rem', opacity: 0.4 }}>🅰</div>
                    <div style={{ color: '#64748b' }}>Output will appear here after conversion</div>
                </>}
            </div>
        );

        return (
            <ConverterLayout
                toolbar={<>
                    <div className={layoutStyles.formatField}>
                        <label htmlFor="font-from">From format</label>
                        <select id="font-from" value={fromFormat}
                            onChange={e => this.setState({ fromFormat: e.target.value as FontFormat })}>
                            {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                    </div>
                    <div className={layoutStyles.formatField}>
                        <label htmlFor="font-to">To format</label>
                        <select id="font-to" value={toFormat}
                            onChange={e => this.setState({ toFormat: e.target.value as FontFormat })}>
                            {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                    </div>
                </>}

                inputLabel={`Input (${fromFormat.toUpperCase()})`}
                inputMeta={fontBuffer ? `${(fontBuffer.byteLength / 1024).toFixed(1)} KB` : undefined}
                inputArea={inputPanel}
                inputActions={
                    <label className={layoutStyles.fileDrop}>
                        📂 Choose font file
                        <input type="file" accept=".ttf,.otf,.woff,.woff2,.eot,font/*" onChange={this.onFileChange} />
                    </label>
                }

                outputLabel={`Output (${toFormat.toUpperCase()})`}
                outputMeta={outputUrl ? `${(outputSize / 1024).toFixed(1)} KB` : undefined}
                outputArea={outputPanel}
                outputActions={
                    <button className={`${layoutStyles.iconBtn} ${layoutStyles.iconBtnPrimary}`}
                        onClick={this.download} disabled={!outputUrl}>
                        ⬇ Download .{toFormat}
                    </button>
                }

                error={error}

                primaryAction={
                    <button className={layoutStyles.primaryBtn}
                        onClick={this.convert}
                        disabled={!fontBuffer || busy}>
                        {busy ? 'Converting…' : 'Convert →'}
                    </button>
                }
            />
        );
    }
}
